// The small menu opened from a part inside a document (a table handle, a
// callout's color bar, an image's "…", the block handle, the formatting bar's
// block type).
//
// Promise:
// - Placed next to the anchor (to its right, or below it) and kept inside the
//   viewport; `position: fixed`, rendered where it is (inside the document's
//   area, so focus-in-area checks still find it). No portal: the theme tokens
//   live on <html>, so they reach it anyway.
// - Arrow keys move the highlighted item, Enter runs it, Escape and a press
//   outside close it. Items run on mousedown: waiting for click would let the
//   outside press close the menu first.
// - The menu never takes the focus from the body (the caller returns the focus
//   to the body before it goes away); the keys are listened to on
//   the document while it is open.
import {
  type CSSProperties,
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Icon } from "../../ui/icon";
import { placePopover } from "../../ui/popover";

export interface FloatingMenuItem {
  /** Tells items apart when labels may repeat (default: the label). */
  id?: string;
  label: string;
  /** A small heading drawn above this item (not bold, no line): groups a long menu. */
  section?: string;
  /** Removes something (the danger text color). */
  danger?: boolean;
  /** A color swatch (any CSS color): menus that choose a color show it. */
  swatch?: string;
  /** The current choice: marked with a check, never with bold. */
  selected?: boolean;
  onSelect(): void;
}

const GAP = 6;
const MARGIN = 8;

function placeRight(
  anchor: DOMRect,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
): { top: number; left: number } {
  const right = anchor.right + GAP;
  const left =
    right + panel.width <= viewport.width - MARGIN
      ? right
      : Math.max(MARGIN, anchor.left - GAP - panel.width);
  const top = Math.max(
    MARGIN,
    Math.min(anchor.top, viewport.height - panel.height - MARGIN),
  );
  return { top, left };
}

export function FloatingMenu({
  anchor,
  placement,
  items,
  label,
  onClose,
  className = "w-44",
}: {
  anchor: DOMRect;
  placement: "right" | "below";
  items: FloatingMenuItem[];
  /** Read aloud for the menu. */
  label?: string;
  onClose(): void;
  /** The width (default `w-44`). */
  className?: string;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<CSSProperties | null>(null);
  const [active, setActive] = useState(() => {
    const current = items.findIndex((item) => item.selected);
    return current >= 0 ? current : 0;
  });
  // Read by the key listener without re-subscribing on every move.
  const latest = useRef({ items, active, onClose });
  latest.current = { items, active, onClose };

  // frontend-development: not a fetch; measures the panel to place it.
  useLayoutEffect(() => {
    const p = panel.current;
    if (!p) return;
    const size = { width: p.offsetWidth, height: p.scrollHeight };
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    if (placement === "right") setPlace(placeRight(anchor, size, viewport));
    else {
      const at = placePopover(anchor, size, viewport, "start");
      setPlace({ top: at.top, left: at.left, maxHeight: at.maxHeight });
    }
  }, [anchor, placement]);

  // frontend-development: not a fetch; keys and outside presses while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const { items: list, active: at, onClose: close } = latest.current;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setActive((at + 1) % list.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setActive((at - 1 + list.length) % list.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        list[at]?.onSelect();
      }
    };
    const onDown = (e: PointerEvent) => {
      if (panel.current?.contains(e.target as Node)) return;
      latest.current.onClose();
    };
    // Capture: the editor must not see the keys meant for the menu.
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, []);

  return (
    <div
      ref={panel}
      role="menu"
      aria-label={label}
      data-popover=""
      contentEditable={false}
      className={`fixed z-50 ${className} overflow-y-auto [scrollbar-gutter:auto] rounded-[9px] border border-border bg-surface py-1 shadow-[var(--shadow-popover)]`}
      style={place ?? { top: 0, left: 0, visibility: "hidden" }}
    >
      {items.map((item, index) => (
        <Fragment key={item.id ?? item.label}>
          {item.section && (
            <div className="px-3 pt-2 pb-1 text-[11px] text-muted">
              {item.section}
            </div>
          )}
          <button
            type="button"
            // A choice (it has `selected`) is a radio item with its state.
            {...(item.selected === undefined
              ? { role: "menuitem" }
              : { role: "menuitemradio", "aria-checked": item.selected })}
            tabIndex={-1}
            className={`flex h-8 w-full cursor-pointer items-center gap-2 px-3 text-left text-[13px] ${
              index === active ? "bg-hover" : ""
            } ${item.danger ? "text-danger" : "text-body"}`}
            // Runs on mousedown and keeps the body's selection and focus.
            onMouseDown={(e) => {
              e.preventDefault();
              item.onSelect();
            }}
            onMouseEnter={() => setActive(index)}
          >
            {item.swatch && (
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block h-2.5 w-2.5 flex-none rounded-full"
                style={{ backgroundColor: item.swatch }}
              />
            )}
            <span className="pointer-events-none min-w-0 flex-1 truncate">
              {item.label}
            </span>
            {item.selected && <Icon kind="check" size={13} />}
          </button>
        </Fragment>
      ))}
    </div>
  );
}
